import { computeXpReward, type XpReward } from '@jeraisonne/ui';
import type { DemoStep } from '@/features/demo/types';

/**
 * Machine à états PURE de l'autoplayer de `/decouverte` (colonne gauche desktop).
 *
 * Un pas de l'exercice se joue en trois temps :
 *   reading   → l'énoncé + les choix s'affichent, rien de sélectionné
 *   selecting → le choix « joué » se surligne (imite un tap)
 *   revealed  → la bonne réponse est révélée + la popup de feedback s'affiche
 * puis le pas rejoint l'HISTORIQUE et on passe au suivant. Après le dernier pas,
 * on recommence à zéro (historique vidé) → boucle infinie.
 *
 * L'historique s'ACCUMULE (les pas précédents restent visibles, comme le vrai
 * player). Logique isolée ici (sans React) pour rester testable.
 */

export type AutoplayPhase = 'reading' | 'selecting' | 'revealed';

export interface AutoplayState {
  /** Indices des pas déjà terminés, dans l'ordre (toujours `[0 … stepIndex-1]`). */
  history: number[];
  stepIndex: number;
  phase: AutoplayPhase;
}

/** Durée d'affichage (ms) de chaque phase avant de passer à la suivante. */
export const AUTOPLAY_PHASE_MS: Record<AutoplayPhase, number> = {
  reading: 2100,
  selecting: 750,
  revealed: 3400,
};

export const INITIAL_AUTOPLAY_STATE: AutoplayState = {
  history: [],
  stepIndex: 0,
  phase: 'reading',
};

/** État figé (avec un peu d'historique, pas révélé) montré en mouvement réduit. */
export const REDUCED_AUTOPLAY_STATE: AutoplayState = {
  history: [0, 1],
  stepIndex: 2,
  phase: 'revealed',
};

/** Transition pure reading → selecting → revealed → (pas suivant + historique, ou boucle). */
export function advanceAutoplay(state: AutoplayState, totalSteps: number): AutoplayState {
  if (totalSteps <= 0) return INITIAL_AUTOPLAY_STATE;
  switch (state.phase) {
    case 'reading':
      return { ...state, phase: 'selecting' };
    case 'selecting':
      return { ...state, phase: 'revealed' };
    case 'revealed': {
      const isLast = state.stepIndex >= totalSteps - 1;
      if (isLast) return INITIAL_AUTOPLAY_STATE;
      return {
        history: [...state.history, state.stepIndex],
        stepIndex: state.stepIndex + 1,
        phase: 'reading',
      };
    }
  }
}

/**
 * Index du choix « joué » à un pas donné.
 *
 * Par défaut : la bonne réponse. Sur le pas-erreur désigné, un distracteur AVEC
 * `feedback_md` — pour illustrer « chaque erreur est expliquée immédiatement ».
 * Repli sur la bonne réponse si le pas-erreur n'a aucun distracteur commenté.
 */
export function pickedChoiceIndex(
  step: DemoStep,
  stepIndex: number,
  mistakeStepIndex: number,
): number {
  if (stepIndex === mistakeStepIndex) {
    const wrongWithFeedback = step.choices.findIndex((c) => !c.is_correct && !!c.feedback_md);
    if (wrongWithFeedback >= 0) return wrongWithFeedback;
  }
  const correct = step.choices.findIndex((c) => c.is_correct);
  return correct >= 0 ? correct : 0;
}

export interface StepGamification {
  /** Récompense « gamy » du pas `upToIndex` (dernier joué). */
  reward: XpReward;
  /** XP cumulé de la session, du pas 0 au pas `upToIndex` inclus. */
  sessionXp: number;
}

/**
 * Déroule la gamification (série + XP cumulé) du pas 0 au pas `upToIndex` inclus,
 * en jouant le choix déterministe de chaque pas — miroir du `ExerciseScreen`
 * réel (`computeXpReward` plié sur la séquence de bonnes/mauvaises réponses).
 */
export function gamificationUpTo(wasCorrectByStep: boolean[], upToIndex: number): StepGamification {
  let streak = 0;
  let sessionXp = 0;
  let reward = computeXpReward(false, 0);
  for (let i = 0; i <= upToIndex && i < wasCorrectByStep.length; i += 1) {
    reward = computeXpReward(wasCorrectByStep[i]!, streak);
    streak = reward.streak;
    sessionXp += reward.total;
  }
  return { reward, sessionXp };
}
